import { k } from '../../localization/t';

// The Realm Walker's tree, display side. The expedition itself is the
// pure engine under src/utils/game/realmWalker/; this file names the
// nodes and sums what they grant into the perks the engine reads.
//
// Each node's `perks` is what buying it adds. Keys the engine reads:
//   satchel     extra match drops per match
//   provisions  extra starting provisions
//   emberCap    Ember cap above 6
//   emberStart  Ember above 2 at each realm's start
//   vigour      extra Vigour on every Vigour keyword
//   heavy       Heavy threshold on the retinue
//   grievous    Grievous threshold on the retinue
//   candle      extra candles (one wipe survived per candle)
//   reroll      free ordeal rerolls per realm
//   companion   a permanent Levy of the player's choice at set-out
//   taverns     extra units offered per tavern

export const REALMWALKER_NODES = [
  { key: 'realmwalker_setout', title: k('Set Out'), type: 'hub', major: true, x: 60, y: 300,
    desc: k('Open the realm. Fill your satchel in the arena, then set out from camp with a retinue of levies.') },

  // The Pack
  { key: 'realmwalker_forage', title: k('Forage'), type: 'rewards', requires: ['realmwalker_setout'], x: 170, y: 200, perks: { provisions: 1 },
    desc: k('Set out with 1 more provision.') },
  { key: 'realmwalker_larder', title: k('Well-Stocked Larder'), type: 'rewards', requires: ['realmwalker_forage'], x: 280, y: 140, perks: { provisions: 1 },
    desc: k('Set out with 1 more provision.') },
  { key: 'realmwalker_cache', title: k('Hidden Cache'), type: 'rewards', requires: ['realmwalker_larder'], x: 390, y: 90, perks: { provisions: 2 },
    desc: k('Set out with 2 more provisions.') },
  { key: 'realmwalker_satchel', title: k('Deeper Satchel'), type: 'rewards', requires: ['realmwalker_forage'], x: 280, y: 250, perks: { satchel: 1 },
    desc: k('One more drop falls on the board each match, on your eighth turn.') },
  { key: 'realmwalker_haggle', title: k('Word Travels'), type: 'hub', requires: ['realmwalker_larder', 'realmwalker_satchel'], x: 390, y: 200, perks: { taverns: 1 },
    desc: k('Every tavern offers 1 more unit for hire.') },
  { key: 'realmwalker_reputation', title: k('Known on the Road'), type: 'hub', requires: ['realmwalker_haggle'], x: 500, y: 150, perks: { taverns: 1 },
    desc: k('Every tavern offers 1 more unit for hire, again.') },

  // The Ember
  { key: 'realmwalker_kindling', title: k('Kindling'), type: 'gameplay', requires: ['realmwalker_setout'], x: 170, y: 300, perks: { emberStart: 1 },
    desc: k('Each realm begins with 1 more Ember.') },
  { key: 'realmwalker_hearth', title: k('Hearth'), type: 'gameplay', requires: ['realmwalker_kindling'], x: 280, y: 350, perks: { emberCap: 1 },
    desc: k('The Ember cap rises by 1.') },
  { key: 'realmwalker_bright', title: k('Burning Bright'), type: 'gameplay', requires: ['realmwalker_hearth'], excludes: ['realmwalker_deep'], x: 390, y: 300, perks: { emberStart: 1 },
    desc: k('Each realm begins with 1 more Ember. Cannot be held with Banked Deep.') },
  { key: 'realmwalker_deep', title: k('Banked Deep'), type: 'gameplay', requires: ['realmwalker_hearth'], excludes: ['realmwalker_bright'], x: 390, y: 400, perks: { emberCap: 1 },
    desc: k('The Ember cap rises by 1. Cannot be held with Burning Bright.') },
  { key: 'realmwalker_companion', title: k('Old Companion'), type: 'gameplay', major: true, minLevel: 10, requires: ['realmwalker_bright', 'realmwalker_deep'], x: 510, y: 350, perks: { companion: 1 },
    desc: k('A levy of your choice walks with you from set-out, every expedition.') },
  { key: 'realmwalker_drill', title: k('Drilled Retinue'), type: 'gameplay', requires: ['realmwalker_companion'], x: 620, y: 290, perks: { vigour: 1 },
    desc: k('Every unit with a Vigour action carries 1 more Vigour.') },
  { key: 'realmwalker_reading', title: k('Second Reading'), type: 'gameplay', requires: ['realmwalker_companion'], x: 620, y: 410, perks: { reroll: 1 },
    desc: k('Reroll one ordeal per realm for free.') },

  // The Line
  { key: 'realmwalker_temper', title: k('Tempered'), type: 'gameplay', requires: ['realmwalker_setout'], x: 170, y: 420, perks: { heavy: 1 },
    desc: k('Your retinue takes a heavy hit only at 1 higher.') },
  { key: 'realmwalker_iron', title: k('Iron Resolve'), type: 'gameplay', requires: ['realmwalker_temper'], x: 280, y: 480, perks: { grievous: 1 },
    desc: k('Your retinue takes a grievous hit only at 1 higher.') },
  { key: 'realmwalker_stoneskin', title: k('Stoneskin'), type: 'gameplay', requires: ['realmwalker_iron'], excludes: ['realmwalker_keenedge'], x: 390, y: 530, perks: { heavy: 1 },
    desc: k('Heavy rises by 1 more. Cannot be held with Keen Edge.') },
  { key: 'realmwalker_keenedge', title: k('Keen Edge'), type: 'gameplay', requires: ['realmwalker_iron'], excludes: ['realmwalker_stoneskin'], x: 500, y: 480, perks: { grievous: 1 },
    desc: k('Grievous rises by 1 more. Cannot be held with Stoneskin.') },
  { key: 'realmwalker_vigil', title: k('Long Vigil'), type: 'gameplay', requires: ['realmwalker_stoneskin', 'realmwalker_keenedge'], x: 620, y: 530, perks: { vigour: 1 },
    desc: k('Every unit with a Vigour action carries 1 more Vigour.') },
  { key: 'realmwalker_candle', title: k('The Second Candle'), type: 'hub', major: true, minLevel: 20, requires: ['realmwalker_drill', 'realmwalker_reading', 'realmwalker_vigil'], x: 740, y: 350, perks: { candle: 1 },
    desc: k('A second candle. Your retinue survives one more total wipe per expedition.') },
  { key: 'realmwalker_lantern', title: k('Lantern Bearer'), type: 'gameplay', requires: ['realmwalker_candle'], x: 860, y: 290, perks: { reroll: 1 },
    desc: k('Reroll one more ordeal per realm for free.') },
];

const PERK_KEYS = ['satchel', 'provisions', 'emberCap', 'emberStart', 'vigour', 'heavy', 'grievous', 'candle', 'reroll', 'companion', 'taverns'];

export const isRealmWalker = (state) => state?.classKey === 'realmwalker';

// What the purchased nodes grant, summed. Zero across the board for any
// other class, so callers never guard.
export function realmWalkerPerks(state) {
  const perks = Object.fromEntries(PERK_KEYS.map((key) => [key, 0]));
  if (!isRealmWalker(state)) return perks;
  const spent = new Set(state.pointsSpent || []);
  for (const node of REALMWALKER_NODES) {
    if (!spent.has(node.key) || !node.perks) continue;
    for (const [key, value] of Object.entries(node.perks)) perks[key] += value;
  }
  return perks;
}
